import React, { useMemo } from 'react';
import { Flex, Text, Icon, Box } from '@chakra-ui/react';
import { FiCalendar, FiGift, FiClock } from 'react-icons/fi';
import { differenceInDays, differenceInHours, isSameDay, parseISO, addDays, isAfter, startOfDay, getDay } from 'date-fns';

const HolidayCountdown = () => {
  // Define Federal Holidays for 2025 with exact dates and mobile-friendly short names
  const holidays = useMemo(() => {
    return [
      { 
        name: "New Year's Day", 
        shortName: "New Year's",
        date: "2025-01-01" 
      },
      { 
        name: "Martin Luther King Jr. Day / Inauguration Day", 
        shortName: "MLK Day",
        date: "2025-01-20" 
      },
      { 
        name: "Washington's Birthday", 
        shortName: "President's Day",
        date: "2025-02-17" 
      },
      { 
        name: "Memorial Day", 
        shortName: "Memorial Day",
        date: "2025-05-26" 
      },
      { 
        name: "Juneteenth National Independence Day", 
        shortName: "Juneteenth",
        date: "2025-06-19" 
      },
      { 
        name: "Independence Day", 
        shortName: "4th of July",
        date: "2025-07-04" 
      },
      { 
        name: "Labor Day", 
        shortName: "Labor Day",
        date: "2025-09-01" 
      },
      { 
        name: "Columbus Day", 
        shortName: "Columbus Day",
        date: "2025-10-13" 
      },
      { 
        name: "Veterans Day", 
        shortName: "Veterans Day",
        date: "2025-11-11" 
      },
      { 
        name: "Thanksgiving Day", 
        shortName: "Thanksgiving",
        date: "2025-11-27" 
      },
      { 
        name: "Christmas Day", 
        shortName: "Christmas",
        date: "2025-12-25" 
      }
    ];
  }, []);

  // Find today's holiday or the next upcoming one
  const { isHolidayToday, nextHoliday, daysUntilNextHoliday, hoursUntilNextHoliday } = useMemo(() => {
    const today = new Date();
    const todayStart = startOfDay(today);
    const tomorrow = addDays(todayStart, 1);
    
    // Check if today is a holiday
    const todayHoliday = holidays.find(holiday => 
      isSameDay(parseISO(holiday.date), today)
    );
    
    if (todayHoliday) {
      return { 
        isHolidayToday: true, 
        nextHoliday: todayHoliday,
        daysUntilNextHoliday: 0,
        hoursUntilNextHoliday: 0
      };
    }
    
    // Find the next upcoming holiday
    const futureHolidays = holidays
      .filter(holiday => isAfter(parseISO(holiday.date), todayStart))
      .sort((a, b) => parseISO(a.date) - parseISO(b.date));
    
    // If no future holidays this year, use the first holiday of next year
    if (futureHolidays.length === 0) {
      const nextYearNewYear = { 
        name: "New Year's Day", 
        shortName: "New Year's",
        date: `${new Date().getFullYear() + 1}-01-01` 
      };
      
      return {
        isHolidayToday: false,
        nextHoliday: nextYearNewYear,
        daysUntilNextHoliday: differenceInDays(parseISO(nextYearNewYear.date), todayStart),
        hoursUntilNextHoliday: differenceInHours(parseISO(nextYearNewYear.date), today)
      };
    }
    
    const next = futureHolidays[0];
    const nextHolidayDate = parseISO(next.date);
    const nextHolidayStart = startOfDay(nextHolidayDate);
    const days = differenceInDays(nextHolidayStart, todayStart);
    const hours = differenceInHours(nextHolidayDate, today);
    
    // Check if the holiday is exactly tomorrow (next calendar day)
    const isTomorrow = isSameDay(nextHolidayStart, tomorrow);
    
    return {
      isHolidayToday: false,
      nextHoliday: next,
      daysUntilNextHoliday: days,
      hoursUntilNextHoliday: hours,
      isTomorrow
    };
  }, [holidays]);

  // If no holidays found (shouldn't happen with our setup)
  if (!nextHoliday) return null;

  // Determine what message to show based on time until holiday
  // Uses responsive text with shorter holiday names on mobile to prevent overflow
  const getMessage = () => {
    const getHolidayName = (useShort = false) => {
      return useShort ? nextHoliday.shortName : nextHoliday.name;
    };

    if (isHolidayToday) {
      return (
        <>
          {/* Mobile version with shorter text */}
          <Text 
            fontSize="sm" 
            fontWeight="medium" 
            m={0} 
            p={0} 
            lineHeight="1.2"
            display={{ base: "block", md: "none" }}
          >
            Today is{" "}
            <Text 
              as="span" 
              fontWeight="bold" 
              fontSize="inherit" 
              color="var(--color-accent-light)" 
              _dark={{ color: "var(--color-accent-dark)" }} 
              display="inline"
            >
              {getHolidayName(true)}
            </Text>!
          </Text>
          
          {/* Desktop version with full text */}
          <Text 
            fontSize="sm" 
            fontWeight="medium" 
            m={0} 
            p={0} 
            lineHeight="1"
            display={{ base: "none", md: "block" }}
          >
            Today is{" "}
            <Text 
              as="span" 
              fontWeight="bold" 
              fontSize="inherit" 
              color="var(--color-accent-light)" 
              _dark={{ color: "var(--color-accent-dark)" }} 
              display="inline"
            >
              {getHolidayName(false)}
            </Text>!
          </Text>
        </>
      );
    } else if (daysUntilNextHoliday === 0) {
      // Less than 24 hours away
      return (
        <>
          {/* Mobile version */}
          <Text 
            fontSize="sm" 
            fontWeight="medium" 
            m={0} 
            p={0} 
            lineHeight="1.2"
            display={{ base: "block", md: "none" }}
          >
            <Text 
              as="span" 
              fontWeight="bold" 
              fontSize="inherit" 
              color="var(--color-accent-light)" 
              _dark={{ color: "var(--color-accent-dark)" }} 
              display="inline"
            >
              {hoursUntilNextHoliday}h
            </Text>{" "}
            until {getHolidayName(true)}
          </Text>
          
          {/* Desktop version */}
          <Text 
            fontSize="sm" 
            fontWeight="medium" 
            m={0} 
            p={0} 
            lineHeight="1"
            display={{ base: "none", md: "block" }}
          >
            <Text 
              as="span" 
              fontWeight="bold" 
              fontSize="inherit" 
              color="var(--color-accent-light)" 
              _dark={{ color: "var(--color-accent-dark)" }} 
              display="inline"
            >
              {hoursUntilNextHoliday}
            </Text>{" "}
            hours until {getHolidayName(false)}
          </Text>
        </>
      );
    } else if (daysUntilNextHoliday === 1) {
      // Tomorrow
      return (
        <>
          {/* Mobile version */}
          <Text 
            fontSize="sm" 
            fontWeight="medium" 
            m={0} 
            p={0} 
            lineHeight="1.2"
            display={{ base: "block", md: "none" }}
          >
            <Text 
              as="span" 
              fontWeight="bold" 
              fontSize="inherit" 
              color="var(--color-accent-light)" 
              _dark={{ color: "var(--color-accent-dark)" }} 
              display="inline"
            >
              Tomorrow
            </Text>{" "}
            is {getHolidayName(true)}!
          </Text>
          
          {/* Desktop version */}
          <Text 
            fontSize="sm" 
            fontWeight="medium" 
            m={0} 
            p={0} 
            lineHeight="1"
            display={{ base: "none", md: "block" }}
          >
            <Text 
              as="span" 
              fontWeight="bold" 
              fontSize="inherit" 
              color="var(--color-accent-light)" 
              _dark={{ color: "var(--color-accent-dark)" }} 
              display="inline"
            >
              Tomorrow
            </Text>{" "}
            is {getHolidayName(false)}!
          </Text>
        </>
      );
    } else {
      // More than a day away
      return (
        <>
          {/* Mobile version */}
          <Text 
            fontSize="sm" 
            fontWeight="medium" 
            m={0} 
            p={0} 
            lineHeight="1.2"
            display={{ base: "block", md: "none" }}
          >
            <Text 
              as="span" 
              fontWeight="bold" 
              fontSize="inherit" 
              color="var(--color-accent-light)" 
              _dark={{ color: "var(--color-accent-dark)" }} 
              display="inline"
            >
              {daysUntilNextHoliday}
            </Text>{" "}
            days until {getHolidayName(true)}
          </Text>
          
          {/* Desktop version */}
          <Text 
            fontSize="sm" 
            fontWeight="medium" 
            m={0} 
            p={0} 
            lineHeight="1"
            display={{ base: "none", md: "block" }}
          >
            <Text 
              as="span" 
              fontWeight="bold" 
              fontSize="inherit" 
              color="var(--color-accent-light)" 
              _dark={{ color: "var(--color-accent-dark)" }} 
              display="inline"
            >
              {daysUntilNextHoliday}
            </Text>{" "}
            days until {getHolidayName(false)}
          </Text>
        </>
      );
    }
  };

  return (
    <Flex 
      align="center" 
      gap={1.5}
      py={0}
      px={0}
      borderRadius="full"
      bg="var(--color-secondary-light)/10"
      _dark={{
        bg: "var(--color-secondary-dark)/10",
      }}
      display="inline-flex"
      maxW="fit-content"
      height="100%"
      m={0}
      pl={2}
      pr={2.5}
    >
      <Icon
        as={isHolidayToday ? FiGift : daysUntilNextHoliday === 0 ? FiClock : FiCalendar}
        boxSize={{ base: 3, md: 3 }}
        color="var(--color-accent-light)"
        _dark={{
          color: "var(--color-accent-dark)"
        }}
        flexShrink={0}
        m={0}
      />
      
      <Box lineHeight="1" m={0} p={0}>
        {getMessage()}
      </Box>
    </Flex>
  );
};

export default HolidayCountdown; 